import {
  faRefresh,
  faSave,
  faArrowDownToLine,
  faSpinner,
} from "@fortawesome/pro-regular-svg-icons";
import { OnSelectHandler } from "react-day-picker";

import { Button } from "src/base-components/Button";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { SearchBox } from "src/base-components/SearchBox";
import { Tooltip } from "src/design-system/Tooltip";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";

type Props = {
  onTermChange: (term: string) => void;
  onDateRangePickerChange: OnSelectHandler<DateRange | undefined>;
  onClickRefresh: () => void;
  isRefreshing: boolean;
  onClickSave: () => void;
  isSaving: boolean;
  disableSave?: "unavailable" | "unsaveable_dataset";
  onClickDownload: (format: "json" | "csv") => void;
  isDownloading: boolean;
  disableDownload: boolean;
  selectedTab: "sandbox" | "live";
  initialSearchTerm: string;
  disableDateRangePicker: boolean;
  dateRangePickerValue?: DateRange;
};

export const Filters: React.FC<Props> = ({
  onTermChange,
  onClickRefresh,
  isRefreshing,
  isSaving,
  isDownloading,
  onClickSave,
  onClickDownload,
  disableDownload,
  disableSave,
  selectedTab,
  initialSearchTerm,
  onDateRangePickerChange,
  dateRangePickerValue,
  disableDateRangePicker,
}) => {
  const downloadButton = (
    <Button disabled={disableDownload} variant="secondary" fullWidth>
      <Icon
        icon={isDownloading ? faSpinner : faArrowDownToLine}
        size="2xs"
        spin={isDownloading}
      />
    </Button>
  );
  const { entitiesEnabled } = useWorkspaceFeatureGates();

  return (
    <div className="px-6">
      <div className="mb-4 flex flex-row items-center gap-2">
        <Tooltip
          placement="top"
          title={
            entitiesEnabled
              ? "Search by Decision ID"
              : "Search by Decision ID or Entity ID"
          }
          asChild
        >
          <div className="w-full cursor-default">
            <SearchBox
              defaultValue={initialSearchTerm}
              placeholder="Search..."
              onChange={onTermChange}
            />
          </div>
        </Tooltip>
        <div className="w-[206px] flex-shrink-0">
          <DateRangePicker
            disabled={disableDateRangePicker}
            value={dateRangePickerValue}
            onChange={onDateRangePickerChange}
          />
        </div>
        <Tooltip placement="top" title="Refresh" asChild>
          <span className="inline-block h-8 w-8">
            <Button
              dataLoc="refresh-button"
              disabled={isRefreshing}
              variant="secondary"
              fullWidth
              onClick={() => onClickRefresh()}
            >
              <Icon icon={faRefresh} size="2xs" />
            </Button>
          </span>
        </Tooltip>
        {selectedTab === "live" && (
          <>
            <Tooltip placement="top" title="Download as dataset" asChild>
              <span className="inline-block h-8 w-8">
                {disableDownload ? (
                  downloadButton
                ) : (
                  <OptionsDropdown
                    buttonDataLoc="dataset-item-download-options"
                    elements={[
                      {
                        key: "Download as CSV",
                        action: () => onClickDownload("csv"),
                      },
                      {
                        key: "Download as JSON",
                        action: () => onClickDownload("json"),
                      },
                    ]}
                  >
                    {downloadButton}
                  </OptionsDropdown>
                )}
              </span>
            </Tooltip>
            <Tooltip
              placement="right"
              title={
                disableSave === "unsaveable_dataset"
                  ? "Please try adjusting the date filter to be able to save a dataset. Data from before 1 Apr 2023 cannot be used in a dataset"
                  : "Save all as test dataset"
              }
              asChild
            >
              <span className="inline-block h-8 w-8">
                <Button
                  disabled={!!disableSave}
                  variant="secondary"
                  fullWidth
                  onClick={() => onClickSave()}
                >
                  <Icon
                    icon={isSaving ? faSpinner : faSave}
                    size="2xs"
                    spin={isSaving}
                  />
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
